import React, { useCallback, useEffect, useRef, useState } from 'react';
import VisibleRatingStars from '../../components/common/visible_rating_stars';
import moment from 'moment';
import { Button } from '../../components/common/button';
import { faRightToBracket } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TPublishedReviewsRowProps } from './types';
import { replaceNewlinesWithBreakElements } from '../../utils/text';

const PublishedReviewsRow = ({ addCard, cards, reviewInfo }: TPublishedReviewsRowProps) => {
    const ref = useRef(null);
    const [isValidSnippet, setIsValidSnippet] = useState(false);
    const [isSelectable, setIsSelectable] = useState(true);
    const [infoTextEl, setInfoTextEl] = useState(<p><strong>Highlight your favorite part of the review (max 100 chars)</strong></p>);

    const maxSnippetLength = 100;

    useEffect(() => {
        const isReviewAlreadyUsedAsSnippet = cards.some(cardItem => cardItem.reviewContext.sourceReviewID === reviewInfo.ReviewID);
        const hasReachedMaxCards = cards.length >= 5; 

        if (isReviewAlreadyUsedAsSnippet) {
            setInfoTextEl(<strong className="mb-3 block">Already being used as a showcase review</strong>);
            setIsValidSnippet(false);
            setIsSelectable(false);
            return;
        } 

        if (hasReachedMaxCards) {
            setInfoTextEl(<strong className="mb-3 block">Maximum Number of Review Showcases Reached</strong>);
            setIsValidSnippet(false);
            setIsSelectable(false);
            return;
        }

        setInfoTextEl(<strong className="mb-3 block">Highlight your favorite part of the review (max 100 chars)</strong>);
        setIsValidSnippet(false);
        setIsSelectable(true);
        
    }, [cards]);

    useEffect(() => {

        const controller = new AbortController();

        document.addEventListener('selectionchange', () => {

            const selectionText = document.getSelection().toString().trim();

            if (isSelectable && !selectionText) {
                setInfoTextEl(<strong className="mb-3 block">Highlight your favorite part of the review (max 100 chars)</strong>);
                setIsValidSnippet(false);
            }

        }, { signal: controller.signal });

        return () => {
            controller.abort();
        };

    }, [isSelectable]);

    const validateSelection = useCallback(() => {
        
        if (!isSelectable) {
            return;
        }

        const selection = document.getSelection();
        const selectionText = selection.toString().trim();
        const selectionLength = selectionText.length;

        // only run for selected text with same reviewID
        if (selection && selection.anchorNode?.parentElement?.parentElement?.dataset?.reviewid === reviewInfo.ReviewID.toString()) {

            if (selectionText && selectionLength <= maxSnippetLength) {

                if (cards.some(cardItem => cardItem.reviewContext.sourceReviewID === reviewInfo.ReviewID)) {
                    setInfoTextEl(<strong className="mb-3 block">Already being used as a showcase review</strong>);
                    setIsValidSnippet(false);
                    return;
                }

                setInfoTextEl(<>Showcase this review</>);
                setIsValidSnippet(true);
                return;
            }
            
            if (selectionText && selectionLength > maxSnippetLength) {
                setInfoTextEl(<strong className="mb-3 block">Highlight your favorite part of the review (<span className="text-red-500">max 100 chars</span>)</strong>);
                setIsValidSnippet(false);
                return;
            }
        }

        setInfoTextEl(<strong className="mb-3 block">Highlight your favorite part of the review (max 100 chars)</strong>);
        setIsValidSnippet(false);

    }, [cards, isSelectable]);

    const copySnippet = () => {
        const selection = window.getSelection();
        const selectionText = selection.toString().trim();
        
        addCard({
            reviewContext: {
                sourceReviewID: reviewInfo.ReviewID,
                sourceReviewerDisplayName: reviewInfo.ReviewerDisplayName,
                sourceReviewConnectionName: reviewInfo.ConnectionName
            },
            snippetID: 0,
            snippetText: selectionText,
        });
        setIsValidSnippet(false);
    };

    return (
        <div ref={ref} className="flex flex-col border border-brand_grey me-3 rounded-md">
            <ul className="text-sm flex flex-row gap-5 bg-brand_faint-grey p-3 rounded-t-md">
                <li>
                    {reviewInfo.ReviewerDisplayName && `${reviewInfo.ReviewerDisplayName}`} 
                </li>
                <li>
                    {moment(reviewInfo.LastPublishedDate).isValid() ? moment(reviewInfo.LastPublishedDate).format('DD MMM YYYY') : ''}
                </li>
                <li>
                    (Review ID {reviewInfo.ReviewID})
                </li>
                <li className="ms-auto">
                    <VisibleRatingStars 
                        keyProp={reviewInfo.ReviewID} 
                        value={reviewInfo.OverallRating}
                    />                    
                </li>
            </ul>
            <div className="p-3">
                {!isValidSnippet || !isSelectable ? <>{infoTextEl}</> :
                    <Button 
                        className="btn btn-outline btn-outline-tertiary mb-3"
                        disabled={!isValidSnippet}
                        onClick={copySnippet}
                    >
                        {infoTextEl} 
                        {<FontAwesomeIcon className="ms-2" icon={faRightToBracket} size='lg'/>}
                    </Button>
                } 
                <span 
                    ref={ref} 
                    role='textbox'
                    data-reviewid={reviewInfo.ReviewID}
                    draggable={false}
                    onMouseUp={validateSelection}
                    onMouseEnter={validateSelection}
                    onMouseMove={(event) => {
                        const selectionLength = window.getSelection().toString().trim().length;
                        if (selectionLength > maxSnippetLength) {
                            event.currentTarget.className = 'text-md leading-normal selection:bg-red-500';
                            return;
                        }
                        if (selectionLength <= maxSnippetLength) {
                            event.currentTarget.className = 'text-md leading-normal selection:bg-green-500';
                            return;
                        }
                    }}
                    onBlur={validateSelection} 
                    className="text-md leading-normal selection:bg-green-500"
                >
                    {replaceNewlinesWithBreakElements(reviewInfo.ReviewContent)}
                </span>
            </div>
        </div>
    );
};

export default PublishedReviewsRow;
